import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import EventApi from "../../Api/EventApi";
import { Event } from "../../Models/Event";
import { ConferenceMeeting } from "../../Models/ConferenceMeeting";
import Loader from "../Common/Loader";
import { format, parseISO, isValid } from "date-fns";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";
import AddEditEventModal from "./AddEditEventModal";
import { Button, Label, Modal, Textarea, TextInput } from "flowbite-react";
import { HiPencilAlt } from "react-icons/hi";
import { toast } from "react-toastify";
import { patchMeetingDetails } from "../../Api/ScheduleApi";
import {
  normalizeZone,
  parseUtcInstant,
  wallClockToUtcIso,
  zoneAbbrevAt,
  zoneDisplayName,
} from "../../Utils/timeZone";

/** Combine eventDate + eventTime in the conference zone, then convert to UTC for sorting. */
function getEventStartMs(ev: Event): number {
  const dateStr = ev.eventDate;
  const timeStr = ev.eventTime;
  if (!dateStr) return 0;
  try {
    const datePart = dateStr.includes("T") ? dateStr.split("T")[0] : dateStr;
    const [y, mo, d] = datePart.split("-").map(Number);
    if (!y || !mo || !d) return 0;
    let h = 0;
    let m = 0;
    if (timeStr && /^\d{1,2}:\d{2}/.test(timeStr.trim())) {
      const parts = timeStr.trim().split(":").map(Number);
      h = parts[0] ?? 0;
      m = parts[1] ?? 0;
    }
    const date = `${y.toString().padStart(4, "0")}-${mo.toString().padStart(2, "0")}-${d.toString().padStart(2, "0")}`;
    const time = `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    return Date.parse(wallClockToUtcIso(date, time, ev.timeZoneId));
  } catch {
    return 0;
  }
}

function formatEventDate(ev: Event): string {
  const dateStr = ev.eventDate;
  if (!dateStr) return "";
  try {
    const d = dateStr.includes("T") ? parseISO(dateStr) : new Date(dateStr + "T12:00:00Z");
    return isValid(d) ? format(d, "EEEE, MMMM d, yyyy") : "";
  } catch {
    return dateStr;
  }
}

function formatEventTime(ev: Event): string {
  const timeStr = ev.eventTime;
  if (!timeStr) return "";
  try {
    const parts = timeStr.trim().split(":").map(Number);
    const h = parts[0] ?? 0;
    const m = parts[1] ?? 0;
    const d = new Date(2000, 0, 1, h, m);
    return isValid(d) ? format(d, "h:mm a") : timeStr;
  } catch {
    return timeStr;
  }
}

function formatEventEndTime(ev: Event): string {
  const timeStr = ev.eventEndTime ?? null;
  if (!timeStr) return "";
  try {
    const parts = timeStr.trim().split(":").map(Number);
    const h = parts[0] ?? 0;
    const m = parts[1] ?? 0;
    const d = new Date(2000, 0, 1, h, m);
    return isValid(d) ? format(d, "h:mm a") : timeStr;
  } catch {
    return timeStr;
  }
}

function stripHtml(html: string | null | undefined, maxLen: number): string {
  if (!html) return "";
  const text = html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen) + "…";
}

function formatMeetingEt(
  startIso: string,
  endIso: string,
  timeZoneId?: string | null
): { dateLine: string; timeLine: string } {
  const zone = normalizeZone(timeZoneId);
  const s = parseUtcInstant(startIso);
  const e = parseUtcInstant(endIso);
  const dateLine = s.toLocaleDateString("en-US", {
    timeZone: zone,
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const t1 = s.toLocaleTimeString("en-US", { timeZone: zone, hour: "numeric", minute: "2-digit" });
  const t2 = e.toLocaleTimeString("en-US", { timeZone: zone, hour: "numeric", minute: "2-digit" });
  return { dateLine, timeLine: `${t1} – ${t2} ${zoneAbbrevAt(startIso, zone)}` };
}

type TimelineItem =
  | { kind: "event"; id: number; startMs: number; event: Event }
  | { kind: "meeting"; id: number; startMs: number; meeting: ConferenceMeeting };

export default function ConferenceSchedulePage() {
  const { id } = useParams<{ id: string }>();
  const conferenceId = id ? parseInt(id, 10) : NaN;
  const { userModel } = useAppSelector((state) => state.user);
  const [conference, setConference] = useState<Event | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [meetings, setMeetings] = useState<ConferenceMeeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  const [meetingEditOpen, setMeetingEditOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<ConferenceMeeting | null>(null);
  const [editLocation, setEditLocation] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [savingMeeting, setSavingMeeting] = useState(false);
  const [downloadingAgenda, setDownloadingAgenda] = useState(false);

  const isSuperAdmin = userModel?.userLevelId === UserLevelNames.SuperAdmin;
  const userId = userModel?.id;

  const downloadAgendaPdf = async () => {
    if (!conferenceId || isNaN(conferenceId)) return;
    try {
      setDownloadingAgenda(true);
      const response = await EventApi.getConferenceAgendaPdf(conferenceId);
      const pdfBlob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(pdfBlob);
      const tempLink = document.createElement("a");
      tempLink.href = url;
      const safeName = (conference?.name ?? "Conference").replace(/[\\/:*?"<>|]+/g, " ").trim();
      tempLink.setAttribute("download", `${safeName} Agenda.pdf`);
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading agenda PDF:", error);
      toast.error("Unable to download agenda PDF.");
    } finally {
      setDownloadingAgenda(false);
    }
  };

  const reload = useCallback(async () => {
    if (!conferenceId || isNaN(conferenceId) || !id) return;
    setLoading(true);
    try {
      const [confRes, subRes, meetRes] = await Promise.all([
        EventApi.getEvent(id).then((r) => r.data).catch(() => null),
        EventApi.getConferenceSubEvents(conferenceId).then((r) => r.data ?? []).catch(() => []),
        EventApi.getConferenceMeetings(conferenceId).then((r) => r.data ?? []).catch(() => []),
      ]);
      setConference(confRes ?? null);
      const sorted = [...(subRes ?? [])].sort((a, b) => getEventStartMs(a) - getEventStartMs(b));
      setEvents(sorted);
      const activeMeetings = (meetRes ?? []).filter((m) => m.status === 0);
      setMeetings(activeMeetings.sort((a, b) => parseUtcInstant(a.startUtc).getTime() - parseUtcInstant(b.startUtc).getTime()));
    } finally {
      setLoading(false);
    }
  }, [conferenceId, id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const timeline = useMemo(() => {
    const items: TimelineItem[] = [];
    for (const ev of events) {
      items.push({ kind: "event", id: ev.id, startMs: getEventStartMs(ev), event: ev });
    }
    for (const m of meetings) {
      const startMs = parseUtcInstant(m.startUtc).getTime();
      items.push({ kind: "meeting", id: m.id, startMs, meeting: m });
    }
    items.sort((a, b) => a.startMs - b.startMs);
    return items;
  }, [events, meetings]);

  const canEditMeeting = (m: ConferenceMeeting) =>
    isSuperAdmin ||
    (!!userId && (m.hostUserId === userId || m.guestUserId === userId));

  const openMeetingEditor = (e: React.MouseEvent, m: ConferenceMeeting) => {
    e.stopPropagation();
    setEditingMeeting(m);
    setEditLocation(m.location ?? "");
    setEditNotes(m.notes ?? "");
    setMeetingEditOpen(true);
  };

  const saveMeetingEdits = async () => {
    if (!editingMeeting) return;
    setSavingMeeting(true);
    try {
      const res = await patchMeetingDetails(editingMeeting.id, {
        location: editLocation || null,
        notes: editNotes || null,
      });
      if (res.success) {
        toast.success(res.message ?? "Meeting updated.");
        setMeetingEditOpen(false);
        setEditingMeeting(null);
        await reload();
      } else {
        toast.error(res.message ?? "Update failed.");
      }
    } catch {
      toast.error("Failed to update meeting.");
    } finally {
      setSavingMeeting(false);
    }
  };

  const openEventEditor = (e: React.MouseEvent, ev: Event) => {
    e.stopPropagation();
    setSelectedEvent(ev);
    setShowEventModal(true);
  };

  if (loading) return <Loader />;
  if (!conference) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <p className="text-gray-600">Conference not found.</p>
        <Link to="/" className="text-blue-600 hover:underline mt-2 inline-block">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const title = conference.name ?? "Conference Schedule";

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <Link to="/" className="text-gray-600 hover:text-gray-900 text-sm mb-2 inline-block">
          ← Dashboard
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
        <p className="text-gray-600 mt-1">
          Schedule includes conference sessions and member meetings. Click a row for full details.
          All times are {zoneDisplayName(conference.timeZoneId)}.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            color="light"
            disabled={downloadingAgenda || timeline.length === 0}
            onClick={downloadAgendaPdf}
          >
            {downloadingAgenda ? "Preparing PDF…" : "Download printable agenda"}
          </Button>
        </div>
      </div>

      {timeline.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-gray-600">
          No schedule items yet.
        </div>
      ) : (
        <div className="relative">
          <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-gray-200" aria-hidden />

          <ul className="space-y-0">
            {timeline.map((item, index) => {
              const rowKey = item.kind === "event" ? `event-${item.id}` : `meeting-${item.id}`;
              const isExpanded = expandedKey === rowKey;

              if (item.kind === "event") {
                const ev = item.event;
                const dateLabel = formatEventDate(ev);
                const startTime = formatEventTime(ev);
                const endTime = formatEventEndTime(ev);
                const datePart = ev.eventDate?.includes("T") ? ev.eventDate.split("T")[0] : ev.eventDate;
                const abbr = datePart && ev.eventTime
                  ? zoneAbbrevAt(wallClockToUtcIso(datePart, ev.eventTime.slice(0, 5), ev.timeZoneId), ev.timeZoneId)
                  : "";
                const clock = startTime && endTime ? `${startTime} – ${endTime}` : startTime || endTime;
                const timeLabel = clock && abbr ? `${clock} ${abbr}` : clock;
                const locDisplay = ev.location?.trim() || "—";
                const notesPreview = stripHtml(ev.description, 220);

                return (
                  <li key={rowKey} className="relative flex gap-4 pl-10 pb-6">
                    <div
                      className="absolute left-0 w-8 h-8 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center text-xs font-medium text-gray-600 shadow-sm z-10"
                      aria-hidden
                    >
                      {index + 1}
                    </div>
                    <div
                      className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
                      onClick={() => setExpandedKey(isExpanded ? null : rowKey)}
                    >
                      <div className="p-4">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 min-w-0">
                            {timeLabel && (
                              <span className="text-sm font-semibold text-blue-700 tabular-nums">{timeLabel}</span>
                            )}
                            {dateLabel && <span className="text-sm text-gray-500">{dateLabel}</span>}
                          </div>
                          {isSuperAdmin && (
                            <Button
                              size="xs"
                              color="light"
                              className="shrink-0"
                              onClick={(e) => openEventEditor(e, ev)}
                            >
                              <HiPencilAlt className="mr-1 h-4 w-4" />
                              Edit
                            </Button>
                          )}
                        </div>
                        <h2 className="mt-1 text-lg font-semibold text-gray-900">{ev.name ?? "Untitled"}</h2>
                        {ev.eventType === "ContactSession" && (
                          <Link
                            to={`/events/${ev.id}`}
                            className="text-sm text-blue-600 hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View contact session map
                          </Link>
                        )}
                        <p className="mt-1 text-sm text-gray-600">
                          <span className="font-medium">Location:</span> {locDisplay}
                        </p>
                        <p className="mt-1 text-sm text-gray-600">
                          <span className="font-medium">Notes:</span>{" "}
                          {notesPreview || <span className="text-gray-400 italic">None</span>}
                        </p>
                        {isExpanded && ev.description && (
                          <div
                            className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-700 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: ev.description }}
                          />
                        )}
                        {isExpanded && !ev.description && (
                          <p className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-500 italic">
                            No additional details.
                          </p>
                        )}
                        <p className="mt-2 text-xs text-gray-400">{isExpanded ? "Click to collapse" : "Click to expand"}</p>
                      </div>
                    </div>
                  </li>
                );
              }

              const m = item.meeting;
              const { dateLine, timeLine } = formatMeetingEt(m.startUtc, m.endUtc, conference.timeZoneId);
              const locDisplay = m.location?.trim() || "—";
              const notesDisplay = m.notes?.trim() || "—";
              const showEdit = canEditMeeting(m);

              return (
                <li key={rowKey} className="relative flex gap-4 pl-10 pb-6">
                  <div
                    className="absolute left-0 w-8 h-8 rounded-full bg-emerald-50 border-2 border-emerald-400 flex items-center justify-center text-xs font-medium text-emerald-800 shadow-sm z-10"
                    aria-hidden
                  >
                    M
                  </div>
                  <div
                    className="flex-1 min-w-0 rounded-lg border border-emerald-200 bg-white shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
                    onClick={() => setExpandedKey(isExpanded ? null : rowKey)}
                  >
                    <div className="p-4">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <span className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Meeting</span>
                          <div className="text-sm font-semibold text-blue-700 tabular-nums mt-0.5">{timeLine}</div>
                          <div className="text-sm text-gray-500">{dateLine}</div>
                        </div>
                        {showEdit && (
                          <Button
                            size="xs"
                            color="light"
                            className="shrink-0"
                            onClick={(e) => openMeetingEditor(e, m)}
                          >
                            <HiPencilAlt className="mr-1 h-4 w-4" />
                            Edit
                          </Button>
                        )}
                      </div>
                      <h2 className="mt-2 text-lg font-semibold text-gray-900">
                        {m.hostMemberName} ↔ {m.guestMemberName}
                      </h2>
                      <p className="mt-1 text-sm text-gray-600">
                        <span className="font-medium">Location:</span> {locDisplay}
                      </p>
                      <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">
                        <span className="font-medium">Notes:</span> {notesDisplay}
                      </p>
                      {isExpanded && (
                        <p className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-400">
                          One-on-one meeting between member companies shown above.
                        </p>
                      )}
                      <p className="mt-2 text-xs text-gray-400">{isExpanded ? "Click to collapse" : "Click to expand"}</p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <AddEditEventModal
        show={showEventModal}
        setShow={setShowEventModal}
        selectedEvent={selectedEvent}
        setselectedEvent={setSelectedEvent}
        getEvents={reload}
      />

      <Modal show={meetingEditOpen} onClose={() => !savingMeeting && setMeetingEditOpen(false)} size="lg">
        <Modal.Header>Edit meeting</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Update location and notes for this meeting. Time changes are not available here.
            </p>
            <div>
              <Label htmlFor="mtg-loc">Location</Label>
              <TextInput
                id="mtg-loc"
                value={editLocation}
                onChange={(e) => setEditLocation(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="mtg-notes">Notes</Label>
              <Textarea
                id="mtg-notes"
                rows={5}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="gray" onClick={() => setMeetingEditOpen(false)} disabled={savingMeeting}>
            Cancel
          </Button>
          <Button className="buttonMain" onClick={saveMeetingEdits} disabled={savingMeeting}>
            {savingMeeting ? "Saving…" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
